/**
 * PRD.289/292 — Shared prescription-image pipeline.
 *
 * Renders the exact encounter prescription document (`PrescriptionPrintLayout`)
 * offscreen at A4 and rasterizes it to a PNG via the existing `renderNodeToPng`
 * pipeline. The same PNG is used as the on-screen preview AND the download
 * payload. Extracted so every pharmacy surface (the read-only drawer, the New
 * Pharmacy Sale reference panel) shows the identical document — one renderer,
 * no duplication.
 *
 * `captureNode` MUST be rendered by the consumer somewhere OUTSIDE any
 * `transform`ed ancestor (a drawer's slide transform breaks `position: fixed`),
 * e.g. at the top of the component tree or inside a body-level portal.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qAppConfig,
  qAppointmentProviders,
  qEncounterById,
  qPatientById,
  qPrescriptionPrintSettings,
  qPrescriptionsByEncounter,
  qUserProviderLink,
  qVitalsByEncounterId,
  resolvePrintLetterhead,
  formatVitalsSummary,
  buildPrintVitals,
  type AppConfigRow,
  type AppointmentProviderRow,
  type PatientDetailRow,
  type PatientEncounterRow,
  type PatientPrescriptionRow,
  type PatientVitalEntryWithDefinitionRow,
  type PrescriptionPrintSettingsRow,
  type UserProviderLinkRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { useBrandTokens } from '../../shell/shared';
import {
  PrescriptionPrintLayout,
  type PrintPrescriptionItem,
} from '../clinic/prescription-print/PrescriptionPrintLayout';
import { useEncounterPrintClinical } from '../clinic/prescription-print/useEncounterPrintClinical';
import {
  formatPatientAge,
  resolveDoctor,
  resolvePrintSettings,
} from '../clinic/prescription-print/print-utils';
import { renderNodeToPng } from '../../lib/prescription-image';

export interface UsePrescriptionImageResult {
  /** Rasterized PNG data-URL — inline preview + download payload. */
  imgUrl: string | null;
  /** True while html2canvas is producing the image. */
  rendering: boolean;
  /** No clinician-confirmed prescription items to render. */
  printDisabled: boolean;
  /** Trigger a browser download of the rendered PNG. */
  download: () => void;
  /** Offscreen A4 capture node — render this somewhere non-transformed. */
  captureNode: ReactNode;
}

export function usePrescriptionImage(
  active: boolean,
  patientId: string,
  encounterId: string,
  patientNameFallback?: string | null,
): UsePrescriptionImageResult {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { user, claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';

  const { data: rxRows = [] } = usePowerSyncQuery<PatientPrescriptionRow>(
    qPrescriptionsByEncounter(encounterId),
  );
  const { data: printSettingsRows = [] } = usePowerSyncQuery<PrescriptionPrintSettingsRow>(
    qPrescriptionPrintSettings(),
  );
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const { data: providerLinkRows = [] } = usePowerSyncQuery<UserProviderLinkRow>(
    qUserProviderLink(orgId, userId),
  );
  const { data: providerRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: patientRows = [] } = usePowerSyncQuery<PatientDetailRow>(qPatientById(patientId));
  const { data: vitalsRows = [] } = usePowerSyncQuery<PatientVitalEntryWithDefinitionRow>(
    qVitalsByEncounterId(encounterId),
  );
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncounterById(encounterId),
  );
  const encounter = encounterRows[0];

  const printSettings = useMemo(
    () => resolvePrintSettings(printSettingsRows[0] ?? null, appConfigRows[0] ?? null),
    [printSettingsRows, appConfigRows],
  );
  const printBranding = useMemo(
    () => resolvePrintLetterhead(appConfigRows[0] ?? null),
    [appConfigRows],
  );
  const printVitalsSummary = useMemo(() => formatVitalsSummary(vitalsRows), [vitalsRows]);
  const printComplaint = encounter?.chief_complaint ?? '';
  // PRD.320 — Dental Structured clinical content, composed from canonical
  // encounter domains by the SAME hook every other print path uses.
  const printVitals = useMemo(() => buildPrintVitals(vitalsRows), [vitalsRows]);
  const printClinical = useEncounterPrintClinical(encounterId, patientId, printComplaint);
  const printDoctor = useMemo(() => {
    const linkedProviderId = providerLinkRows[0]?.provider_id ?? null;
    const linkedProvider = linkedProviderId
      ? (providerRows.find((p) => p.id === linkedProviderId) ?? null)
      : null;
    return resolveDoctor(linkedProvider, user?.displayName ?? null);
  }, [providerLinkRows, providerRows, user?.displayName]);
  const printPatient = useMemo(() => {
    const p = patientRows[0];
    return {
      name: p?.full_name ?? patientNameFallback ?? '',
      age: formatPatientAge(p), // PRD.315 — falls back to reported age
      sex: p?.sex ?? '',
    };
  }, [patientRows, patientNameFallback]);

  // PRD.217 §12 — exclude AI drafts; only clinician-confirmed lines print.
  const printItems: PrintPrescriptionItem[] = useMemo(
    () =>
      rxRows
        .filter((r) => (r.status ?? 'final') === 'final')
        .map((r) => {
          const snapshotDetails = [
            r.brand_name_snapshot,
            r.generic_name_snapshot,
            r.strength_snapshot,
            r.dosage_form_snapshot,
          ]
            .filter(Boolean)
            .join(' · ');
          return {
            itemName:
              r.item_name_snapshot ?? r.free_text_name ?? r.item_name ?? 'Untitled medicine',
            itemDetails: r.item_details ?? snapshotDetails,
            intakeTiming: r.intake_timing ?? '',
            doseQuantity: r.dose_quantity ?? r.default_dosage_snapshot ?? '',
            scheduleMorning: r.schedule_morning === 1,
            scheduleAfternoon: r.schedule_afternoon === 1,
            scheduleNight: r.schedule_night === 1,
            durationValue: r.duration_value ?? null,
            durationUnit: r.duration_unit ?? '',
            instructions: r.instructions ?? r.default_instruction_snapshot ?? '',
          };
        }),
    [rxRows],
  );

  const printDate = encounter?.started_at ?? encounter?.created_at ?? new Date().toISOString();
  const printDisabled = printItems.length === 0;
  const patientLoaded = patientRows.length > 0;

  const captureRef = useRef<HTMLDivElement | null>(null);
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const renderedKeyRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!active) {
      renderedKeyRef.current = null;
      inFlightRef.current = false;
      setImgUrl(null);
      setRendering(false);
    }
  }, [active]);
  useEffect(() => {
    renderedKeyRef.current = null;
    setImgUrl(null);
  }, [patientId, encounterId]);

  useEffect(() => {
    if (!active || printDisabled || !patientLoaded) return;
    const key = `${patientId}:${encounterId}`;
    if (renderedKeyRef.current === key || inFlightRef.current) return;
    inFlightRef.current = true;
    setRendering(true);
    const raf = requestAnimationFrame(() => {
      const node = captureRef.current;
      if (!node) {
        inFlightRef.current = false;
        setRendering(false);
        return;
      }
      renderNodeToPng({ node })
        .then((png) => {
          if (png.dataUrl) {
            setImgUrl(png.dataUrl);
            renderedKeyRef.current = key;
          }
        })
        .catch((err) => {
          console.error('[usePrescriptionImage] render failed', err);
        })
        .finally(() => {
          inFlightRef.current = false;
          setRendering(false);
        });
    });
    return () => cancelAnimationFrame(raf);
  }, [active, printDisabled, patientLoaded, patientId, encounterId]);

  const download = () => {
    if (!imgUrl) return;
    const safeName = (printPatient.name || 'patient').replace(/[^\w.-]+/g, '_');
    const date = printDate.slice(0, 10);
    const a = document.createElement('a');
    a.href = imgUrl;
    a.download = `Prescription-${safeName}-${date}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const captureNode =
    active && !printDisabled ? (
      <div
        ref={captureRef}
        aria-hidden
        style={{
          position: 'fixed',
          left: '-99999px',
          top: 0,
          width: '210mm',
          background: '#ffffff',
          pointerEvents: 'none',
        }}
      >
        <PrescriptionPrintLayout
          settings={printSettings}
          doctor={printDoctor}
          patient={printPatient}
          date={printDate}
          items={printItems}
          complaint={printComplaint}
          vitalsSummary={printVitalsSummary}
          clinical={printClinical}
          vitals={printVitals}
          accentColor={primary}
          fontColor={printBranding.fontColor}
          letterhead={printBranding.letterhead}
        />
      </div>
    ) : null;

  return { imgUrl, rendering, printDisabled, download, captureNode };
}
